/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { CircularProgress, Stack, Typography } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import Section from "../../common/Section/Section";
import { buildLocationPinMapField } from "../../../fields/locationPinMap";
import { buildSatelliteImageryFields } from "../../../fields/satelliteImagery";
import type { AssessmentSectionComponentProps } from "../../types";
import {
  checkImageAvailability,
  getAssessmentReportImageUrls,
} from "../imageAvailability";

const bucketUrl = import.meta.env.VITE_ASSESSMENT_REPORT_IMAGE_BUCKET_URL as
  string | undefined;

function ImagesSection({ details, onEdit }: AssessmentSectionComponentProps) {
  const [imageCheck, setImageCheck] = useState<{
    solutionId: string;
    availability: Record<string, boolean>;
  } | null>(null);
  const availability =
    imageCheck?.solutionId === details.id ? imageCheck.availability : {};
  const imageUrls = {
    ...(bucketUrl ? getAssessmentReportImageUrls(details.id, bucketUrl) : {}),
  };

  useEffect(() => {
    let active = true;
    void Promise.all(
      Object.entries(imageUrls).map(
        async ([key, url]) => [key, await checkImageAvailability(url)] as const,
      ),
    ).then((results) => {
      if (active)
        setImageCheck({
          solutionId: details.id,
          availability: Object.fromEntries(results),
        });
    });
    return () => {
      active = false;
    };
  }, [details.id]);

  return (
    <Section
      title="Images"
      columns={1}
      onEdit={() => onEdit("images")}
      fields={[
        buildLocationPinMapField(details.locationPinMapLink, 0),
        ...buildSatelliteImageryFields(details.satelliteImagery, 1, true),
      ]}
      aboveFields={
        <Stack spacing={2} sx={{ mb: 3 }}>
          {(
            [
              ["spider", "Spider Diagram"],
              ["project", "Project Image"],
              ["sdg", "SDG Background Image"],
            ] as const
          ).map(([key, label]) => (
            <Stack key={key} direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ minWidth: 180 }}>{label}</Typography>
              {!(key in imageUrls) ? (
                <CloseIcon
                  fontSize="large"
                  color="error"
                  aria-label={`${label} missing; image bucket not configured`}
                />
              ) : availability[key] === undefined ? (
                <CircularProgress size={20} aria-label={`${label} loading`} />
              ) : availability[key] ? (
                <DoneIcon
                  fontSize="large"
                  color="success"
                  aria-label={`${label} uploaded`}
                />
              ) : (
                <CloseIcon
                  fontSize="large"
                  color="error"
                  aria-label={`${label} missing`}
                />
              )}
            </Stack>
          ))}
        </Stack>
      }
    />
  );
}

export default ImagesSection;
